<template>
  <div class="sk-wrapper">
    <div class="canvas-wrap">
      <svg
        :viewBox="vb.x + ' ' + vb.y + ' ' + vb.w + ' ' + vb.h"
        class="sk-svg"
        preserveAspectRatio="xMinYMin meet"
      >
        <g ref="content">
          <text
            v-for="hdr in geometry.groupHeaders"
            :key="'hdr-' + hdr.group"
            :x="geometry.leftX"
            :y="hdr.y"
            class="sk-grouphdr"
            :fill="hdr.color"
          >{{ hdr.text }}</text>

          <g v-for="node in geometry.leftBlocks" :key="'l-' + node.code">
            <rect
              :x="geometry.leftX"
              :y="node.y0"
              :width="geometry.nodeW"
              :height="node.h"
              rx="4"
              :fill="node.color"
            />
            <text
              :x="geometry.leftX - 12"
              :y="node.y0 + node.h / 2 + 4"
              text-anchor="end"
              class="sk-leftlabel"
            >{{ node.label }}</text>
          </g>

          <g v-for="node in geometry.rightBlocks" :key="'r-' + node.effect">
            <rect
              :x="geometry.rightX"
              :y="node.y0"
              :width="geometry.nodeW"
              :height="node.h"
              rx="4"
              :class="node.noModel ? 'sk-noderect--nomodel' : 'sk-noderect'"
            />
            <text
              :x="geometry.rightX + geometry.nodeW + 12"
              :y="node.y0 + node.h / 2 - 2"
              :class="node.noModel ? 'sk-rightname--nomodel' : 'sk-rightname'"
            >{{ node.label }}</text>
            <text
              :x="geometry.rightX + geometry.nodeW + 12"
              :y="node.y0 + node.h / 2 + 14"
              class="sk-rightsub"
            >{{ node.subtitle }}</text>
          </g>

          <path
            v-for="ribbon in geometry.ribbons"
            :key="ribbon.id"
            :d="ribbon.d"
            :fill="ribbon.color"
            opacity="0.5"
            :stroke="ribbon.strokeColor"
            :stroke-width="ribbon.strokeWidth"
            :stroke-dasharray="ribbon.negative ? '8,4' : '0'"
            :stroke-opacity="ribbon.negative ? 1 : 0.9"
            class="sk-ribbon"
            v-tooltip.top="ribbon.tooltipConfig"
          />
        </g>
      </svg>
    </div>

    <div v-if="noModelEffects.length" class="sk-warn-list">
      <div v-for="item in noModelEffects" :key="item.effect" class="sk-warn">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" class="sk-warn-icon">
          <path
            d="M12 9v4m0 4h.01M10.29 3.86l-8.4 14.5A1.5 1.5 0 0 0 3.18 20.5h17.64a1.5 1.5 0 0 0 1.3-2.14l-8.4-14.5a1.5 1.5 0 0 0-2.6 0Z"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <div><b>{{ item.label }} — sin modelo.</b> {{ item.text }}</div>
      </div>
    </div>

    <details class="sk-legend" open>
      <summary>Leyenda de nodos y cintas</summary>
      <div class="sk-legend-body">
        Cada nombre de la izquierda es una condición intralaboral que quedó seleccionada en
        <em>al menos un</em> análisis principal, coloreada por su dominio dentro de la prueba de
        FRP Intralaborales (Liderazgo y relaciones sociales, Control sobre el trabajo, Demandas
        del trabajo, o Recompensas). El grosor de cada cinta es proporcional a |β|, es decir, a
        mayor grosor es mayor la asociación estadística entre las dos variables. Borde sólido =
        relación positiva, borde punteado = negativa.
      </div>
    </details>
  </div>
</template>

<script>
import { buildSankeyData, getConditionLabel, confidenceLabel, roundRSquaredAdj } from "./regression-helpers";

// design.md §4.2 — mismos 4 colores validados en el Artifact para los
// sub-dominios de Intralaboral, no una paleta nueva inventada aquí.
const GROUP_META = {
  "Liderazgo y relaciones sociales": { hex: "#2f6fb0" },
  "Control sobre el trabajo": { hex: "#1a9a8f" },
  "Demandas del trabajo": { hex: "#c9701f" },
  Recompensas: { hex: "#a03d90" },
};
const DEFAULT_GROUP_HEX = "#9246df";

const CONFIDENCE_COLORS = {
  "alta confianza": { bg: "#d9ead3", border: "#c3eab5", ink: "#3d6b3f" },
  "confianza moderada": { bg: "#fff2cc", border: "#f6db8a", ink: "#8a6d1f" },
  "confianza baja": { bg: "#facca5", border: "#f6b884", ink: "#95591f" },
};

const NODE_W = 22;
const LEFT_X = 170;
const RIGHT_X = 730;
const W = 900;
const TOP = 36;
const BOTTOM_MARGIN = 30;
const MIN_H = 460;
// Más chico que el `thickScale:170` del Artifact — su mock nunca pasó de 6
// nodos izquierdos; con datos reales (hasta 16 condiciones posibles) el
// alto total crece solo por tener más filas, así que se compensa acá para
// que la vista siga cabiendo razonablemente en pantalla sin perder la
// comparabilidad relativa entre betas (sigue siendo una escala fija única
// para los 3 efectos, solo con un factor menor).
const THICK_SCALE = 115;
const LEFT_GAP = 4;
const LEFT_BLOCK_GAP = 11;
const LEFT_GROUP_HEADER_GAP = 14;
const RIGHT_GAP = 3;
const RIGHT_BLOCK_GAP = 26;
const RIGHT_EMPTY_H = 90;
const VB_PADDING = 12;

function segH(beta) {
  return Math.max(8, Math.min(70, Math.abs(beta) * THICK_SCALE));
}

function fmtBeta(beta) {
  if (beta == null) return "—";
  return (beta >= 0 ? "+" : "−") + Math.abs(beta).toFixed(2);
}

function groupHex(group) {
  return (GROUP_META[group] || { hex: DEFAULT_GROUP_HEX }).hex;
}

// El borde punteado (β negativo) se perdía contra el relleno de la propia
// cinta, mismo color a menor opacidad — un oscurecido del mismo tono lo hace
// visible sin introducir un color nuevo ajeno a la paleta del dominio.
function darken(hex, factor) {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const channels = [m[1], m[2], m[3]].map(function(h) {
    const v = Math.round(parseInt(h, 16) * factor);
    return Math.max(0, Math.min(255, v))
      .toString(16)
      .padStart(2, "0");
  });
  return "#" + channels.join("");
}

function ribbonPath(x0, y0a, y0b, x1, y1a, y1b) {
  const mx = (x0 + x1) / 2;
  return (
    "M" + x0 + "," + y0a +
    " C" + mx + "," + y0a + " " + mx + "," + y1a + " " + x1 + "," + y1a +
    " L" + x1 + "," + y1b +
    " C" + mx + "," + y1b + " " + mx + "," + y0b + " " + x0 + "," + y0b + " Z"
  );
}

export default {
  name: "SankeyDiagram",
  props: {
    results: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      vb: { x: 0, y: 0, w: W, h: MIN_H },
    };
  },
  mounted() {
    this.measureViewBox();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.measureViewBox);
    }
  },
  watch: {
    results() {
      this.$nextTick(this.measureViewBox);
    },
  },
  computed: {
    sankeyData() {
      return buildSankeyData(this.results);
    },
    noModelEffects() {
      return this.sankeyData.rightNodes
        .filter(function(n) {
          return n.noModel;
        })
        .map((n) => {
          const analysis = this.results[n.effect];
          const warnings = (analysis && analysis.warnings) || [];
          const text =
            warnings.join(" ") ||
            "Muestra insuficiente. No entra ninguna cinta hacia ese nodo — se deja explícito en vez de ocultarlo.";
          return { effect: n.effect, label: n.label, text: text };
        });
    },
    geometry() {
      const data = this.sankeyData;
      const groupByCode = {};
      data.leftNodes.forEach(function(n) {
        groupByCode[n.code] = n.group;
      });
      const labelByEffect = {};
      data.rightNodes.forEach(function(n) {
        labelByEffect[n.effect] = n.label;
      });
      const links = data.links.map(function(l, i) {
        return Object.assign({}, l, {
          _id: i,
          sourceGroup: groupByCode[l.source],
          targetLabel: labelByEffect[l.target],
        });
      });

      const leftRaw = data.leftNodes.map((node) => {
        const flows = links.filter(function(l) {
          return l.source === node.code;
        });
        const h = flows.reduce(function(sum, f) {
          return sum + segH(f.beta);
        }, 0) + LEFT_GAP * Math.max(0, flows.length - 1);
        return { node: node, flows: flows, h: h };
      });

      const rightRaw = data.rightNodes.map((node) => {
        const flows = links.filter(function(l) {
          return l.target === node.effect;
        });
        const h = flows.length
          ? flows.reduce(function(sum, f) {
              return sum + segH(f.beta);
            }, 0) + RIGHT_GAP * Math.max(0, flows.length - 1)
          : RIGHT_EMPTY_H;
        return { node: node, flows: flows, h: h };
      });

      let cursor = TOP;
      let lastRawGroup = null;
      leftRaw.forEach((b) => {
        if (lastRawGroup !== null && b.node.group !== lastRawGroup) {
          cursor += LEFT_GROUP_HEADER_GAP;
        }
        b.y0Raw = cursor;
        cursor += b.h + LEFT_BLOCK_GAP;
        lastRawGroup = b.node.group;
      });
      const leftUsedH = leftRaw.length ? cursor - LEFT_BLOCK_GAP - TOP : 0;

      cursor = TOP;
      rightRaw.forEach((b) => {
        b.y0Raw = cursor;
        cursor += b.h + RIGHT_BLOCK_GAP;
      });
      const rightUsedH = rightRaw.length ? cursor - RIGHT_BLOCK_GAP - TOP : 0;

      const H = Math.max(MIN_H, TOP + Math.max(leftUsedH, rightUsedH) + BOTTOM_MARGIN);
      const bottom = H - BOTTOM_MARGIN;

      const leftOffset = Math.max(0, (bottom - TOP - leftUsedH) / 2);
      const rightOffset = Math.max(0, (bottom - TOP - rightUsedH) / 2);

      const leftYByLink = {};
      const groupHeaders = [];
      let lastGroup = null;
      const leftBlocks = leftRaw.map((b) => {
        const y0 = b.y0Raw + leftOffset;
        if (b.node.group !== lastGroup) {
          groupHeaders.push({
            group: b.node.group,
            text: b.node.group.toUpperCase(),
            color: groupHex(b.node.group),
            y: y0 - 11,
          });
          lastGroup = b.node.group;
        }
        let fy = y0;
        b.flows.forEach((flow) => {
          const fh = segH(flow.beta);
          leftYByLink[flow._id] = { y0: fy, y1: fy + fh };
          fy += fh + LEFT_GAP;
        });
        return {
          code: b.node.code,
          label: b.node.label,
          group: b.node.group,
          color: groupHex(b.node.group),
          y0: y0,
          h: b.h,
        };
      });

      const rightYByLink = {};
      const rightBlocks = rightRaw.map((b) => {
        const y0 = b.y0Raw + rightOffset;
        let fy = y0;
        b.flows.forEach((flow) => {
          const fh = segH(flow.beta);
          rightYByLink[flow._id] = { y0: fy, y1: fy + fh };
          fy += fh + RIGHT_GAP;
        });
        const analysis = this.results[b.node.effect];
        const r2 = roundRSquaredAdj(analysis);
        const n = analysis && analysis.n != null ? analysis.n : null;
        const subtitle = b.node.noModel
          ? "sin modelo" + (n != null ? " · n=" + n : "")
          : "R² aj. " + r2 + "%" + (n != null ? " · n=" + n : "");
        return {
          effect: b.node.effect,
          label: b.node.label,
          noModel: b.node.noModel,
          y0: y0,
          h: b.h,
          subtitle: subtitle,
        };
      });

      const ribbons = links.map((link) => {
        const ly = leftYByLink[link._id];
        const ry = rightYByLink[link._id];
        const color = groupHex(link.sourceGroup);
        const conf = confidenceLabel(link.p_value);
        const confChip = conf
          ? '<div class="rn-tt-row"><span>Confiabilidad</span><span class="rn-tt-chip" style="background:' +
            CONFIDENCE_COLORS[conf].bg +
            ";border-color:" +
            CONFIDENCE_COLORS[conf].border +
            ";color:" +
            CONFIDENCE_COLORS[conf].ink +
            '">' +
            conf +
            "</span></div>"
          : "";
        const content =
          "<b>" + getConditionLabel(link.source) + "</b>" +
          '<div class="rn-tt-sub">Intralaboral · relación con ' + link.targetLabel + "</div>" +
          '<div class="rn-tt-row"><span>β estandarizado</span><span>' + fmtBeta(link.beta) + "</span></div>" +
          confChip;
        const negative = link.beta < 0;
        return {
          id: link._id,
          d: ribbonPath(
            LEFT_X + NODE_W,
            ly.y0,
            ly.y1,
            RIGHT_X,
            ry.y0,
            ry.y1,
          ),
          color: color,
          negative: negative,
          strokeColor: negative ? darken(color, 0.55) : color,
          strokeWidth: negative ? 2 : 1,
          tooltipConfig: { content: content, html: true, classes: ["rn-tooltip"] },
        };
      });

      return {
        leftX: LEFT_X,
        rightX: RIGHT_X,
        nodeW: NODE_W,
        groupHeaders: groupHeaders,
        leftBlocks: leftBlocks,
        rightBlocks: rightBlocks,
        ribbons: ribbons,
      };
    },
  },
  methods: {
    measureViewBox() {
      const g = this.$refs.content;
      if (!g) return;
      let bbox;
      try {
        bbox = g.getBBox();
      } catch (e) {
        return;
      }
      if (!bbox || bbox.width < 10 || bbox.height < 10) return;
      const x = Math.floor(bbox.x - VB_PADDING);
      const y = Math.floor(bbox.y - VB_PADDING);
      const next = {
        x: x,
        y: y,
        w: Math.ceil(bbox.x + bbox.width + VB_PADDING) - x,
        h: Math.ceil(bbox.y + bbox.height + VB_PADDING) - y,
      };
      if (
        next.x !== this.vb.x ||
        next.y !== this.vb.y ||
        next.w !== this.vb.w ||
        next.h !== this.vb.h
      ) {
        this.vb = next;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sk-wrapper {
  width: 100%;
  max-width: 1040px;
}

.canvas-wrap {
  overflow-x: auto;
}

.sk-svg {
  display: block;
  width: 100%;
  height: auto;
  min-width: 560px;
  font-family: "Inter", sans-serif;
}

.sk-grouphdr {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.sk-leftlabel {
  font-size: 12px;
  font-weight: 600;
  fill: #241f2e;
}

.sk-noderect {
  fill: #241f2e;
}

.sk-noderect--nomodel {
  fill: #f4cdcd;
  stroke: #f6adad;
  stroke-width: 2;
  stroke-dasharray: 5, 5;
}

.sk-rightname {
  font-size: 13px;
  font-weight: 700;
  fill: #241f2e;
}

.sk-rightname--nomodel {
  font-size: 13px;
  font-weight: 700;
  fill: #9b3d3d;
}

.sk-rightsub {
  font-size: 10.5px;
  fill: #948da3;
}

.sk-ribbon {
  cursor: pointer;
}

.sk-warn-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
}

.sk-warn {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #f4cdcd;
  border: 1px solid #f6adad;
  color: #a12a2a;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12.5px;

  b {
    font-weight: 700;
  }
}

.sk-warn-icon {
  flex: none;
  margin-top: 1px;
}

.sk-legend {
  margin-top: 14px;
  font-size: 12.5px;

  summary {
    cursor: pointer;
    color: #5c5568;
    font-weight: 600;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 6px;

    &::-webkit-details-marker {
      display: none;
    }

    &:before {
      content: "›";
      display: inline-block;
      transition: transform 0.15s;
      color: #948da3;
    }
  }

  &[open] summary:before {
    transform: rotate(90deg);
  }
}

.sk-legend-body {
  margin-top: 10px;
  padding: 12px 14px;
  background: #f4f0ee;
  border-radius: 8px;
  color: #5c5568;
}
</style>



// WEBPACK FOOTER //
// src/components/frpm/components/variables-prioritarias/SankeyDiagram.vue